// MockPaperSpecV2 — Sprint 19BH Day 1 Step 1.5 · usage-time mock paper spec.
//
// Why this exists (v4-rewrite.md §12):
//   v3 mock-paper-spec.ts described one of up to 3 mock papers a course
//   could generate during initial ingest (id `paper_[1-3]`). v4 promotes
//   mock generation to USAGE-TIME (MANIFEST.mock_exam.generationTiming='usage')
//   per K17 — initial ingest is short (~5-8min), and mocks are produced when
//   the user clicks "新建" in the Mock 中心 (frontend sprint 19BI).
//
//   The v2 spec is therefore richer:
//     - id is ULID (permanent unique handle, not paper_[1-3])
//     - chaptersFocus / topicsFocus / conceptsFocus restrict scope
//     - wrongAnswerWeighting binds the mock to user attempt history
//       (privacy boundary: frontend aggregates wrong attempts by concept and
//        sends only conceptIds + errorRate — never raw attempts)
//     - mimicPastPaperId optionally selects a past paper to imitate
//     - counts is per-type explicit (mcq / tf / essay / freeResp)
//
// Why this lives BESIDE mock-paper-spec.ts (not replacing):
//   Plan §4.5: "并存旧 mock-paper-spec.ts · 直到 Day 5 alias shim 处理". The
//   old singular CourseDraft.mockPapers[] form still ships in v3 packages on
//   user disks; we keep parsing it until Day 5 IDB migration + frontend
//   integration retires the alias. This file is purely additive.
//
// I17 (永久保留) invariant:
//   Each generated MockPaperResult is keyed by this spec's id and stored in
//   IngestPackage.mockPapers[id]. Users delete only explicitly — no
//   automatic cleanup. The id field MUST be stable across regen/edit.
import { z } from 'zod';
import { OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE } from './curriculum-outline.js';
const OutlineId = () => z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE);
/** Difficulty bias for the generated paper. */
export const MockPaperDifficultyTargetSchema = z.enum(['easy', 'mixed', 'hard']);
/**
 * Wrong-answer driven weighting. Frontend (Mock 中心) aggregates user wrong-
 * answer history into concept-level error rates BEFORE submit — only
 * { conceptId, errorRate } pairs ever reach the backend, never raw attempts.
 */
export const WrongAnswerWeightingSchema = z.object({
    /** Whether the LLM prompt should bias toward weighted concepts. */
    enabled: z.boolean()
        .describe('Master toggle for wrong-answer weighting.'),
    /** Minimum error-rate threshold to include a concept. Default 0.3. */
    minErrorRate: z.number().min(0).max(1).default(0.3)
        .describe('Only concepts with errorRate >= this threshold are weighted heavier. 0–1, default 0.3.'),
    /** Concept ids to weight. Aggregated by frontend; backend trusts as opaque list. */
    sourceConceptIds: z.array(OutlineId()).optional()
        .describe('Concept ids to weight heavier. Frontend pre-filters by minErrorRate.'),
}).passthrough();
/** Per-item-type counts. Each min=0 (a paper can skip a type entirely). */
export const MockPaperCountsSchema = z.object({
    mcq: z.number().int().min(0).max(60)
        .describe('Multiple choice item count. Upper bound 60 = ~1h continuous answering.'),
    tf: z.number().int().min(0).max(40)
        .describe('True/false item count. Upper bound 40.'),
    essay: z.number().int().min(0).max(8)
        .describe('Essay prompt count. Upper bound 8 = full IB-style paper.'),
    freeResp: z.number().int().min(0).max(20)
        .describe('Free-response item count. Upper bound 20.'),
}).passthrough();
export const MockPaperSpecV2Schema = z.object({
    /** ULID. Persistent across mock paper lifecycle. */
    id: z.string().min(1).max(64)
        .describe('ULID. Unique across all mocks of all courses; never reused (I17).'),
    /** Owning course (matches CourseDraft.id). */
    courseId: z.string().min(1).max(64)
        .describe('Owning courseId.'),
    /** User-facing title. Default "Mock #{N}" if absent. */
    title: z.string().min(1).max(120)
        .describe('Title shown in Mock 中心 list. Default "Mock #{N}" when not provided.'),
    /** ISO timestamp when this spec was first submitted (Phase 1 of mock gen). */
    generatedAt: z.string().datetime()
        .describe('ISO timestamp when generation was requested.'),
    // ─── Scope filters (all optional) ────────────────────────────────────────
    chaptersFocus: z.array(OutlineId()).optional()
        .describe('Chapter ids to focus on. Empty = full course coverage.'),
    topicsFocus: z.array(OutlineId()).optional()
        .describe('Topic ids to focus on.'),
    conceptsFocus: z.array(OutlineId()).optional()
        .describe('Concept ids to focus on.'),
    // ─── Wrong-answer driven ─────────────────────────────────────────────────
    wrongAnswerWeighting: WrongAnswerWeightingSchema.optional()
        .describe('Optional wrong-answer driven weighting (privacy: aggregated client-side).'),
    // ─── Form ────────────────────────────────────────────────────────────────
    difficultyTarget: MockPaperDifficultyTargetSchema.default('mixed')
        .describe('Bias for the generated paper difficulty. easy/mixed/hard.'),
    mimicPastPaperId: z.string().min(1).max(120).optional()
        .describe('Optional past paper id (matches PastPaper.id) to imitate in structure/style.'),
    counts: MockPaperCountsSchema
        .describe('Per-type item counts. All required (0 means skip that type).'),
}).passthrough();
//# sourceMappingURL=mock-paper-spec-v2.js.map